import React, { useEffect, useState, useCallback } from 'react';
import { apiClient } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/UI/Card';
import LoadingSpinner from '../../components/UI/LoadingSpinner';

interface PipelineRow {
  mainStatus: string;
  subStageId: string | null;
  subStageName: string | null;
  count: number;
}

interface AgentOption {
  id: string;
  name: string;
}

const AdminPipelinePage: React.FC = () => {
  const [rows, setRows] = useState<PipelineRow[]>([]);
  const [agents, setAgents] = useState<AgentOption[]>([]);
  const [agentFilter, setAgentFilter] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiClient.get('/admin/dialer-dashboard')
      .then((res) => {
        const list = (res.data.data?.agents || []).map((a: any) => ({ id: a.id, name: a.name }));
        setAgents(list);
      })
      .catch(() => setAgents([]));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = agentFilter ? { agentId: agentFilter } : {};
      const res = await apiClient.get('/admin/pipeline-stats', { params });
      setRows(res.data.data.rows || []);
    } catch {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [agentFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const totalLeads = rows.reduce((sum, r) => sum + r.count, 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Pipeline Overview</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Lead counts by main status and sub-stage · {totalLeads.toLocaleString()} total
          </p>
        </div>
        <select
          className="input max-w-xs text-sm"
          value={agentFilter}
          onChange={(e) => setAgentFilter(e.target.value)}
        >
          <option value="">All agents</option>
          {agents.map((a) => (
            <option key={a.id} value={a.id}>{a.name}</option>
          ))}
        </select>
      </div>

      <Card>
        <CardHeader><CardTitle>Funnel</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <LoadingSpinner />
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No pipeline data yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground border-b border-border">
                    <th className="py-2 pr-4">Main Status</th>
                    <th className="py-2 pr-4">Sub-Stage</th>
                    <th className="py-2">Leads</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => (
                    <tr key={i} className="border-b border-border/50">
                      <td className="py-2 pr-4 capitalize">{row.mainStatus.replace(/_/g, ' ')}</td>
                      <td className="py-2 pr-4">{row.subStageName || '—'}</td>
                      <td className="py-2 font-medium">{row.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPipelinePage;
